import React, { useCallback, useState } from 'react'
import clsx from 'clsx'
import { Dispatch, bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { RootActionType, RootState } from 'duck'
import Button from 'components/Button'
import CardCollection, { CardCollectionSkeleton } from 'components/Card/CardCollection'
import Img from 'components/Img'
import Typography from 'components/Typography'
import { MineImage } from 'models/ApiModels'
import { apiSelectors, apiActions } from 'duck/ApiDuck'
import { route } from 'routes'
import { useBreakpoint } from 'utils/Hooks'
import { SimpleList, RenderItemProps } from 'components/ListVirtualized'
import ListLoading from 'components/Loading/ListLoading'
import ListCollected from './ImageList/ListCollected'
import TabsTwo from 'components/Tabs/TabsTwo'
import CardProjectSummary from '../components/CardProjectSummary'
import ArtistDetail from './ArtistDetail'
import Details from './ProjectDetails'
import stylesGlobal from 'stylesGlobal.module.scss'
import styles from '../ProjectPage.module.scss'
import { dialogActions, DialogNames } from 'duck/DialogDuck'
import { cryptoState } from 'utils/CryptoUtils'

const CONFIG_GRID = {
  xs: {
    columns: 2,
    defaultColumnWidth: 170,
    defaultRowHeight: 170,
    gutter: 6
  },
  sm: {
    columns: 3
  },
  default: {
    columns: 5,
    defaultColumnWidth: 250,
    defaultRowHeight: 300,
    gutter: 8
  }
}

const DATA_TABS_SINGLE = [
  { value: 'details', label: 'Details', className: stylesGlobal.PointerEventsNone }
]

const DATA_TABS = [
  { value: 'details', label: 'Details' },
  { value: 'about', label: 'About the Artist' }
]

const INTERNAL_STYLE = {
  ProjectSummaryTopContentInner: clsx(stylesGlobal.WidthFull, styles.ColThumbnail),
  ImageContainer: clsx(
    stylesGlobal.PositionRelative,
    stylesGlobal.DisplayBlock,
    stylesGlobal.WidthFull,
    stylesGlobal.MarginBottom4,
    stylesGlobal.PaddingBottomFull
  ),
  Image: clsx(
    stylesGlobal.PositionAbsolute,
    stylesGlobal.Top0,
    stylesGlobal.Left0,
    stylesGlobal.DisplayBlock,
    stylesGlobal.PointerEventsNone,
    stylesGlobal.UserSelectNone,
    stylesGlobal.ObjectCover
  )
}

const mapStateToProps = (state: RootState) => ({
  openedMineProject: apiSelectors.openedMineProject(state),
  currentUserAddress: apiSelectors.currentUserAddress(state),
  currentMineProjectSampleList: apiSelectors.currentMineProjectSampleList(state),
  currentMineProjectCollectedList: apiSelectors.currentMineProjectCollectedList(state),
  currentMineProjectCuratedList: apiSelectors.currentMineProjectCollectedList(state),
  retrieveProjectLoading: apiSelectors.loading['mineProject.retrieveProject'](state),
  listMineProjectSampleOutputsLoading:
    apiSelectors.loading['mineProject.listMineProjectSampleOutputs'](state)
})

const mapDispatchToProps = (dispatch: Dispatch<RootActionType>) =>
  bindActionCreators(
    {
      setOpenedProjectId: apiActions.mineProject.setOpenedProjectId,
      openDialog: dialogActions.openDialog
    },
    dispatch
  )

type ProjectSummaryProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>

const ProjectSummary: React.FC<ProjectSummaryProps> = props => {
  const {
    openDialog,
    currentUserAddress,
    openedMineProject,
    retrieveProjectLoading,
    currentMineProjectSampleList,
    listMineProjectSampleOutputsLoading
  } = props

  const [activeTab, setActiveTab] = useState(DATA_TABS[0].value)
  const { isMediumscreenAbove, isSmallscreenAbove } = useBreakpoint()
  const loading = retrieveProjectLoading === `${openedMineProject?.id ?? ''}`
  const currentMineProjectSampleListLength = currentMineProjectSampleList?.length || 0
  const network = openedMineProject?.network ?? 'ETHEREUM'
  const type = openedMineProject?.type
  const projectId = openedMineProject?.id
  const showArtistTab =
    Boolean(openedMineProject?.artist_biography) ||
    Boolean(openedMineProject?.instagram_url) ||
    Boolean(openedMineProject?.twitter_url) ||
    Boolean(openedMineProject?.artist_url)

  const renderItem = useCallback(
    (props: RenderItemProps<MineImage>) => {
      const { index, item, measure, width } = props
      if (!item || !width) return null

      return (
        <CardCollection
          network={network}
          hideTitle={true}
          key={item.id || index}
          mineImage={item}
          onLoad={measure}
        />
      )
    },
    [network]
  )

  const onClickArtistCuratedMint = useCallback(() => {
    const isConnectedToCorrectNetwork = cryptoState.network === network

    if (!currentUserAddress) {
      openDialog({
        [DialogNames.CONNECT_TO_METAMASK]: {
          dialogName: DialogNames.CONNECT_TO_METAMASK,
          actionText: 'mint the project'
        }
      })
    } else if (!isConnectedToCorrectNetwork) {
      openDialog({
        [DialogNames.CONNECT_TO_WRONG_NETWORK]: {
          dialogName: DialogNames.CONNECT_TO_WRONG_NETWORK,
          projectNetwork: network ?? 'ETHEREUM'
        }
      })
    } else
      openDialog({
        [DialogNames.MINT_DIALOG_ARTIST_CURATED]: {
          dialogName: DialogNames.MINT_DIALOG_ARTIST_CURATED,
          projectId: projectId ?? 0
        }
      })
  }, [currentUserAddress, network, openDialog, projectId])

  return (
    <div>
      {isSmallscreenAbove ? (
        <CardProjectSummary
          className={stylesGlobal.MarginBottom56}
          loadingMint={loading}
          mineProject={openedMineProject}
          onClickArtistCuratedMint={onClickArtistCuratedMint}
        />
      ) : null}

      <div className={styles.ProjectSummaryTopContent}>
        <div className={INTERNAL_STYLE.ProjectSummaryTopContentInner}>
          <div>
            <div className={INTERNAL_STYLE.ImageContainer}>
              <Img
                image={openedMineProject?.thumbnail}
                className={INTERNAL_STYLE.Image}
                fullWidth
                fullHeight
                showBgColor
              />
            </div>
          </div>

          {!isSmallscreenAbove ? (
            <div className={clsx(stylesGlobal.MarginTop24, stylesGlobal.MarginBottom36)}>
              {type === 'artist' ? (
                <Button
                  disabled={loading}
                  variant="contained"
                  color="secondary"
                  onClick={onClickArtistCuratedMint}
                  fullWidth
                >
                  {'Mint Now'}
                </Button>
              ) : null}

              {type === 'collector' ? (
                <>
                  <Button
                    disabled={loading}
                    variant="contained"
                    color="secondary"
                    to={route.MINE_PROJECTS.getUrl({
                      id: openedMineProject?.id ?? 0,
                      subRoute: 'mine'
                    })}
                    fullWidth
                  >
                    {'Generate NFT'}
                  </Button>

                  <Typography
                    variant="caption"
                    className={stylesGlobal.MarginTop4}
                    align="center"
                    component="h2"
                  >
                    Free to Generate
                  </Typography>
                </>
              ) : null}
            </div>
          ) : null}
        </div>

        {!isSmallscreenAbove ? (
          <CardProjectSummary
            className={stylesGlobal.MarginBottom48}
            loadingMint={loading}
            mineProject={openedMineProject}
            borderless
            onClickArtistCuratedMint={onClickArtistCuratedMint}
          />
        ) : null}

        <div className={styles.ColContent}>
          <TabsTwo
            className={stylesGlobal.MarginBottom32}
            value={activeTab}
            items={showArtistTab ? DATA_TABS : DATA_TABS_SINGLE}
            onChange={(_, value) => {
              setActiveTab(value)
            }}
          />

          {activeTab === 'details' ? <Details /> : null}
          {activeTab === 'about' ? <ArtistDetail /> : null}
        </div>
      </div>

      {openedMineProject?.mine_count ? (
        <div className={isMediumscreenAbove ? stylesGlobal.MarginTop64 : stylesGlobal.MarginTop48}>
          <Typography
            variant="body1"
            component="div"
            fontWeight="bold"
            className={stylesGlobal.MarginBottom16}
          >
            Collected Generations
          </Typography>

          <ListCollected />
        </div>
      ) : null}

      {(openedMineProject &&
        openedMineProject?.mine_count < 10 &&
        currentMineProjectSampleListLength) ||
      listMineProjectSampleOutputsLoading ? (
        <div className={isMediumscreenAbove ? stylesGlobal.MarginTop64 : stylesGlobal.MarginTop48}>
          <Typography
            variant="body1"
            component="div"
            fontWeight="bold"
            className={stylesGlobal.MarginBottom16}
          >
            Sample Generations
          </Typography>

          <div>
            {listMineProjectSampleOutputsLoading && !currentMineProjectSampleListLength ? (
              <ListLoading config={CONFIG_GRID} component={<CardCollectionSkeleton />} items={2} />
            ) : null}

            {currentMineProjectSampleListLength ? (
              <SimpleList
                data={currentMineProjectSampleList?.slice(0, 10)}
                config={CONFIG_GRID}
                renderItem={renderItem}
              />
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(ProjectSummary)
